import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { JobPriority, JobStatus, JobType, PlateManufacturer, Region, User } from '@paragon/contract-library';

import { NormalizedJob } from '../data/jobData';

export interface JobWithUsers extends NormalizedJob {
  readonly creator?: User;
  readonly location?: string;
  readonly assignedTo?: readonly User[];
  readonly openedBy?: User[];
}

export interface JobProperties {
  readonly name?: string;
  readonly selectedRegions: Region[];
  readonly selectedJobType?: JobType;
  readonly selectedJobPriority: JobPriority;
  readonly designProgram?: string;
  readonly designCode?: string | null;
  readonly plateManufacturer?: PlateManufacturer;
  readonly selectedOrganizations: string[] | null;
  readonly comments?: string;
  readonly contactName?: string | null;
  readonly contactPhone?: string | null;
  readonly contactEmail?: string | null;
  readonly billingLocation?: string | null;
  readonly lotNumber?: string | null;
  readonly address?: string | null;
  readonly city?: string | null;
  readonly subdivision?: string | null;
  readonly model?: string | null;
  readonly buildingDesignerName?: string | null;
  readonly buildingDesignerAddress?: string | null;
  readonly buildingDesignerCity?: string | null;
  readonly buildingDesignerState?: string | null;
  readonly buildingDesignerLicenseNumber?: string | null;
  readonly windCode?: string | null;
  readonly windSpeed?: string | null;
  readonly windExposureCategory?: string | null;
  readonly meanRoofHeight?: string | null;
  readonly roofLoad?: string | null;
  readonly floorLoad?: string | null;
  readonly customerName?: string | null;
  readonly convertVerticalsUnderFifteenAndAHalfInchesToSpfNumberTwo?: boolean;
}

interface DraftJob {
  readonly jobBeingEditedId?: number;
  readonly properties: JobProperties;
}

interface JobsState {
  readonly jobs: readonly JobWithUsers[];
  readonly draftJob: DraftJob | null;
  readonly selectedJobIds: readonly number[];
  readonly activeControl: ControlName | null;
}

export type ControlName = 'create' | 'edit' | 'views' | 'manageJobs';

const initialState: JobsState = {
  jobs: [],
  draftJob: null,
  selectedJobIds: [],
  activeControl: null,
};

const jobsSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    deselectAllJobs: (state) => {
      state.selectedJobIds = [];
      state.activeControl = null;
    },
    finishAssigningUsersToJobs: (
      state,
      action: PayloadAction<{
        readonly jobIds: readonly number[];
        readonly users: readonly User[];
      }>,
    ) => {
      const { jobIds, users } = action.payload;
      for (const job of state.jobs) {
        if (jobIds.includes(job.id)) {
          job.assignedTo = castDraft(users);
        }
      }
    },
    finishInsertingJob: (state, action: PayloadAction<JobWithUsers>) => {
      state.jobs.push(castDraft(action.payload));
      state.activeControl = null;
    },
    finishSettingStatus: (
      state,
      action: PayloadAction<{
        readonly jobIds: readonly number[];
        readonly status: JobStatus;
      }>,
    ) => {
      const { jobIds, status } = action.payload;
      for (const job of state.jobs) {
        if (jobIds.includes(job.id)) {
          job.jobStatus = status;
        }
      }
    },
    finishSharingJobsWithOrganizations: (
      state,
      action: PayloadAction<{
        readonly jobIds: readonly number[];
        readonly organizationNames: readonly string[];
      }>,
    ) => {
      const { jobIds, organizationNames } = action.payload;
      for (const job of state.jobs) {
        if (jobIds.includes(job.id)) {
          job.sharedWithOrganizations = castDraft(organizationNames);
        }
      }
    },
    finishUpdatingJob: (
      state,
      action: PayloadAction<{ readonly updatedJob: JobWithUsers; readonly jobProperties: JobProperties }>,
    ) => {
      const { updatedJob, jobProperties } = action.payload;
      state.jobs = castDraft(state.jobs.map((job) => (job.id === updatedJob.id ? updatedJob : job)));
      if (state.draftJob) {
        state.draftJob = { ...state.draftJob, properties: jobProperties };
      }
    },
    onToggleJob: (state, action: PayloadAction<number>) => {
      const jobId = action.payload;
      if (state.selectedJobIds.includes(jobId)) {
        state.selectedJobIds = state.selectedJobIds.filter((id) => id !== jobId);
      } else {
        state.selectedJobIds.push(jobId);
      }
      if (state.selectedJobIds.length > 0) state.activeControl = 'manageJobs';
      else state.activeControl = null;
    },
    setJobs: (state, action: PayloadAction<readonly JobWithUsers[]>) => {
      state.jobs = castDraft(action.payload);
    },
    startCreatingJobWithProperties: (state, action: PayloadAction<JobProperties>) => {
      state.draftJob = { properties: action.payload };
      state.selectedJobIds = [];
      state.activeControl = 'create';
    },
    startEditingJobWithProperties: (state, action: PayloadAction<DraftJob>) => {
      state.draftJob = action.payload;
      state.selectedJobIds = [];
      state.activeControl = 'edit';
    },
    updateDraftJobProperties: (state, action: PayloadAction<Partial<JobProperties>>) => {
      if (state.draftJob) {
        state.draftJob = { ...state.draftJob, properties: { ...state.draftJob.properties, ...action.payload } };
      }
    },
    setActiveControl: (state, action: PayloadAction<ControlName | null>) => {
      state.activeControl = action.payload;
      if (action.payload !== 'edit') state.draftJob = null;
    },
  },
});

export const {
  deselectAllJobs,
  finishAssigningUsersToJobs,
  finishInsertingJob,
  finishSettingStatus,
  finishSharingJobsWithOrganizations,
  finishUpdatingJob,
  onToggleJob,
  setJobs,
  startCreatingJobWithProperties,
  startEditingJobWithProperties,
  updateDraftJobProperties,
  setActiveControl,
} = jobsSlice.actions;
export const jobs = jobsSlice.reducer;
